import { useState, useEffect, useRef, forwardRef, useImperativeHandle } from "react";
import { useTranslation } from "../i18n";
import { api } from "../services/api";
import "./ChannelPicker.css";

const TOPIC_LABELS = { GEO: "GEOPOLITICS", TECH: "TECHNOLOGY", FIN: "FINANCE" };
const TOPIC_COLORS = { GEO: "var(--topic-geo)", TECH: "var(--topic-tech)", FIN: "var(--topic-fin)" };

const TOPIC_TO_CATEGORY = {
  geo: "international_geopolitics",
  tech: "technology",
  fin: "finance",
};

const CATEGORIES = ["technology", "finance", "international_geopolitics", "domestic_news_brazil", "other"];

const PRESET_ICON_MAP = {
  technology:               { gradient: "linear-gradient(135deg, #60a5fa, #3b82f6)", emoji: "\u{1F4BB}" },
  finance:                  { gradient: "linear-gradient(135deg, #4ade80, #22c55e)", emoji: "\u{1F4C8}" },
  international_geopolitics:{ gradient: "linear-gradient(135deg, #fbbf24, #f59e0b)", emoji: "\u{1F30D}" },
  domestic_news_brazil:     { gradient: "linear-gradient(135deg, #f472b6, #ec4899)", emoji: "\u{1F1E7}\u{1F1F7}" },
};
const PRESET_ICON_DEFAULT = { gradient: "linear-gradient(135deg, #a78bfa, #8b5cf6)", emoji: "\u{1F4E1}" };

function getPresetIcon(presetName) {
  const lower = (presetName || "").toLowerCase();
  if (lower.includes("technolog"))   return PRESET_ICON_MAP.technology;
  if (lower.includes("financ") || lower.includes("market")) return PRESET_ICON_MAP.finance;
  if (lower.includes("geopolit"))    return PRESET_ICON_MAP.international_geopolitics;
  if (lower.includes("notic") || lower.includes("geral") || lower.includes("domestic")) return PRESET_ICON_MAP.domestic_news_brazil;
  return PRESET_ICON_DEFAULT;
}

function InfoPopover({ text, dismissLabel }) {
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const handleClick = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [open]);

  return (
    <span className="cp-info-wrapper" ref={wrapperRef}>
      <button
        type="button"
        className="cp-info-icon"
        onClick={() => setOpen((v) => !v)}
        aria-label="Info"
      >
        i
      </button>
      {open && (
        <div className="cp-info-popover">
          <p>{text}</p>
          <button type="button" onClick={() => setOpen(false)}>
            {dismissLabel}
          </button>
        </div>
      )}
    </span>
  );
}

function BlurredOverlay({ children, onUpgrade, upgradeLabel }) {
  return (
    <div className="cp-blurred-wrapper">
      <div className="cp-blurred-content">{children}</div>
      <button type="button" className="cp-upgrade-pill" onClick={onUpgrade}>
        <span className="cp-upgrade-pill-lock">{"\u{1F512}"}</span>
        <span className="cp-upgrade-pill-text">{upgradeLabel}</span>
      </button>
    </div>
  );
}

const ChannelPicker = forwardRef(({
  planPresetLimit = 0,
  planChannelLimit = 0,
  currentPresetCount = 0,
  currentChannelCount = 0,
  showUrlInput = true,
  subscribedChannelIds = [],
  subscribedPresetIds = [],
  onUpgrade,
}, ref) => {
  const { t } = useTranslation();
  const [channels, setChannels] = useState({});
  const [presets, setPresets] = useState([]);
  const [selectedChannels, setSelectedChannels] = useState(new Set());
  const [selectedPresets, setSelectedPresets] = useState(new Set());
  const [urls, setUrls] = useState("");
  const [manualCategory, setManualCategory] = useState("other");
  const [loading, setLoading] = useState(true);

  const presetsLocked = planPresetLimit === 0;
  const channelsLocked = planChannelLimit === 0;
  const bothOpen = !presetsLocked && !channelsLocked;

  const remainingPresets = planPresetLimit === Infinity ? Infinity : planPresetLimit - currentPresetCount;
  const remainingChannels = planChannelLimit === Infinity ? Infinity : planChannelLimit - currentChannelCount;

  useEffect(() => {
    Promise.all([
      api.getFeaturedChannels().catch(() => ({})),
      api.getPresetNames().catch(() => []),
    ]).then(([channelData, presetData]) => {
      setChannels(channelData);
      setPresets(presetData);
      setLoading(false);
    });
  }, []);

  const isChannelSubscribed = (url) =>
    subscribedChannelIds.some((id) => url.includes(id));

  const toggleChannel = (ytId) => {
    if (isChannelSubscribed(ytId)) return;
    setSelectedChannels((prev) => {
      const next = new Set(prev);
      if (next.has(ytId)) {
        next.delete(ytId);
      } else if (next.size < remainingChannels) {
        next.add(ytId);
      }
      return next;
    });
  };

  const togglePreset = (presetId) => {
    if (subscribedPresetIds.includes(presetId)) return;
    setSelectedPresets((prev) => {
      const next = new Set(prev);
      if (next.has(presetId)) {
        next.delete(presetId);
      } else if (next.size < remainingPresets) {
        next.add(presetId);
      }
      return next;
    });
  };

  const TOPIC_KEYS = ["geopolitics", "technology", "finance"];
  const grouped = {};
  for (const key of TOPIC_KEYS) {
    const items = channels[key];
    if (items && items.length > 0) {
      grouped[key.toUpperCase()] = items;
    }
  }

  const channelCategoryMap = {};
  for (const key of TOPIC_KEYS) {
    const items = channels[key];
    if (items) {
      for (const ch of items) {
        channelCategoryMap[ch.url] = TOPIC_TO_CATEGORY[key] || "other";
      }
    }
  }

  useImperativeHandle(ref, () => ({
    getSelection: () => {
      const parsedUrls = urls.split("\n").map((u) => u.trim()).filter(Boolean);
      const categories = {};
      for (const id of selectedChannels) {
        categories[id] = channelCategoryMap[id] || "other";
      }
      for (const url of parsedUrls) {
        categories[url] = manualCategory;
      }
      return {
        selectedPresetIds: Array.from(selectedPresets),
        selectedChannelIds: Array.from(selectedChannels),
        manualUrls: parsedUrls,
        categories,
      };
    },
  }));

  const canSelectMoreChannels = selectedChannels.size < remainingChannels;
  const canSelectMorePresets = selectedPresets.size < remainingPresets;

  if (loading) {
    return (
      <div className="cp-loading">
        <div className="cp-loading-shimmer" />
        <div className="cp-loading-shimmer" />
        <div className="cp-loading-shimmer" />
      </div>
    );
  }

  // ── Presets Section Content ──
  const presetsContent = (
    <div className="cp-preset-section">
      <div className="cp-topic-header">
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <span className="cp-topic-label" style={{ color: "var(--accent)" }}>
            {t("onboarding.presetsLabel")}
          </span>
          <InfoPopover
            text={t("onboarding.presetsInfo")}
            dismissLabel={t("onboarding.presetsInfoDismiss")}
          />
        </div>
        {!presetsLocked && (
          <span className="cp-counter-badge">
            {selectedPresets.size} / {remainingPresets === Infinity ? "\u221E" : remainingPresets}
          </span>
        )}
      </div>

      <div className="cp-preset-grid">
        {presets.map((preset, idx) => {
          const icon = getPresetIcon(preset.category);
          const isLocked = subscribedPresetIds.includes(preset.id);
          const isSelected = isLocked || selectedPresets.has(preset.id);
          const isDisabled = !isSelected && !canSelectMorePresets;
          return (
            <button
              key={preset.id}
              type="button"
              className={
                "cp-preset-card" +
                (isSelected ? " cp-preset-card--selected" : "") +
                (isLocked ? " cp-card--locked" : "") +
                (isDisabled ? " cp-preset-card--disabled" : "")
              }
              onClick={() => togglePreset(preset.id)}
              disabled={isDisabled || isLocked}
            >
              <div className="cp-preset-icon" style={{ background: icon.gradient }}>
                {icon.emoji}
              </div>
              <span className="cp-preset-name">{preset.name}</span>
              <span className="cp-preset-desc">{preset.description}</span>
              {isSelected && <span className="cp-preset-check">{"\u2713"}</span>}
            </button>
          );
        })}
      </div>
    </div>
  );

  // ── Channels Section Content ──
  const channelsContent = (
    <>
      {!channelsLocked && (
        <div className="cp-header">
          <p className="cp-hint">{t("onboarding.suggestionsHint")}</p>
          <span className="cp-counter-badge">
            {selectedChannels.size} / {remainingChannels === Infinity ? "\u221E" : remainingChannels}
          </span>
        </div>
      )}

      {!channelsLocked && !canSelectMoreChannels && selectedChannels.size > 0 && (
        <div className="cp-limit-msg">{t("onboarding.limitReached")}</div>
      )}

      {Object.entries(grouped).map(([topic, chs]) => (
        <div className="cp-topic-section" key={topic}>
          <div className="cp-topic-header">
            <span className="cp-topic-label" style={{ color: TOPIC_COLORS[topic] }}>
              {TOPIC_LABELS[topic] || topic}
            </span>
          </div>
          <div className="cp-grid">
            {chs.map((ch) => {
              const ytId = ch.url;
              const isLocked = isChannelSubscribed(ytId);
              const isSelected = isLocked || selectedChannels.has(ytId);
              const isDisabled = !isSelected && !canSelectMoreChannels;
              return (
                <button
                  key={ytId}
                  className={
                    "cp-card" +
                    (isSelected ? " cp-card--selected" : "") +
                    (isLocked ? " cp-card--locked" : "") +
                    (isDisabled ? " cp-card--disabled" : "")
                  }
                  onClick={() => toggleChannel(ytId)}
                  disabled={isDisabled || isLocked}
                  type="button"
                >
                  {ch.profile_picture ? (
                    <img
                      className="cp-card-img"
                      src={ch.profile_picture.startsWith("data:") ? ch.profile_picture : `data:image/png;base64,${ch.profile_picture}`}
                      alt={ch.name}
                    />
                  ) : (
                    <span className="cp-card-icon">{ch.name[0]}</span>
                  )}
                  <span className="cp-card-name">{ch.name}</span>
                  {isSelected && <span className="cp-card-check">{"\u2713"}</span>}
                </button>
              );
            })}
          </div>
        </div>
      ))}

      {showUrlInput && !channelsLocked && (
        <div className="cp-url-section">
          <div className="cp-url-header">
            <span className="cp-url-title">{t("onboarding.addByUrlTitle")}</span>
            <span className="cp-url-optional">{t("onboarding.addByUrlOptional")}</span>
          </div>
          <div style={{ display: "flex", gap: "10px", alignItems: "flex-start" }}>
            <textarea
              className="cp-url-textarea"
              placeholder={t("onboarding.addByUrlPlaceholder")}
              value={urls}
              onChange={(e) => setUrls(e.target.value)}
              rows={3}
              style={{ flex: 1 }}
            />
            <select
              className="cp-url-textarea"
              value={manualCategory}
              onChange={(e) => setManualCategory(e.target.value)}
              style={{ flex: "0 0 auto", width: "180px", minHeight: "auto", resize: "none" }}
            >
              {CATEGORIES.map((cat) => (
                <option key={cat} value={cat}>{t(`categories.${cat}`)}</option>
              ))}
            </select>
          </div>
        </div>
      )}
    </>
  );

  return (
    <div className="cp-root">
      {/* Presets — selectable or blurred */}
      {presetsLocked ? (
        <BlurredOverlay onUpgrade={onUpgrade} upgradeLabel={t("onboarding.upgradeToUnlock")}>
          {presetsContent}
        </BlurredOverlay>
      ) : (
        presetsContent
      )}

      {/* Divider */}
      <div className="cp-divider">
        <span className="cp-divider-text">
          {bothOpen ? t("onboarding.dividerAnd") : t("onboarding.dividerOr")}
        </span>
      </div>

      {/* Channels — selectable or blurred */}
      {channelsLocked ? (
        <BlurredOverlay onUpgrade={onUpgrade} upgradeLabel={t("onboarding.upgradeToUnlock")}>
          {channelsContent}
        </BlurredOverlay>
      ) : (
        channelsContent
      )}
    </div>
  );
});

ChannelPicker.displayName = "ChannelPicker";

export default ChannelPicker;
